/**
 * Inline-SVG ornaments for the HUD: menu glyphs (24×24, `currentColor` strokes) and the
 * circular class crest. No art assets; tint with CSS `color`.
 */

const NS = 'http://www.w3.org/2000/svg';

/** Parse SVG markup we authored ourselves into an element. */
export function svgEl(markup: string): SVGSVGElement {
  const t = document.createElement('template');
  t.innerHTML = markup.trim();
  return t.content.firstElementChild as SVGSVGElement;
}

const F = 'fill="currentColor" fill-opacity="0.2"';

/** Menu / UI glyph bodies (24×24 viewBox, stroke = currentColor). */
export const HUD_GLYPHS = {
  character: `<circle ${F} cx="12" cy="8" r="3.6"/><path ${F} d="M4.5 20.5c.6-4.3 3.5-6.8 7.5-6.8s6.9 2.5 7.5 6.8z"/>`,
  bag: `<path ${F} d="M5 8.5h14l-1.2 11.8H6.2z"/><path d="M9 8.5V7a3 3 0 0 1 6 0v1.5M9.5 12.5h5"/>`,
  talents: `<path ${F} d="M12 3.5l2.3 4.8 5.2.7-3.8 3.6.9 5.2L12 15.3l-4.6 2.5.9-5.2L4.5 9l5.2-.7z"/><path d="M12 15.3v5.2"/>`,
  map: `<path ${F} d="M3.5 6.5l5.5-2 6 2 5.5-2v13l-5.5 2-6-2-5.5 2z"/><path d="M9 4.5v13M15 6.5v13"/>`,
  settings: `<circle cx="12" cy="12" r="3"/><path ${F} d="M12 2.8l1.6 2.4 2.8-.6.6 2.8 2.4 1.6-1.4 2.5 1.4 2.5-2.4 1.6-.6 2.8-2.8-.6L12 21.2l-1.6-2.4-2.8.6-.6-2.8-2.4-1.6L6 12.5 4.6 10l2.4-1.6.6-2.8 2.8.6z"/>`,
  vault: `<path ${F} d="M4 9.5h16v10H4z"/><path d="M4 9.5 6 5h12l2 4.5M12 12v3.5M10.2 12h3.6"/>`,
  gems: `<path ${F} d="M5.5 9 9 4.5h6L18.5 9 12 20z"/><path d="M5.5 9h13M9 4.5 12 20l3-15.5"/>`,
  rank: `<path ${F} d="M12 3l7 3v5.5c0 4.4-3 7.6-7 9.5-4-1.9-7-5.1-7-9.5V6z"/><path d="M8.5 11l3.5 2.6 3.5-2.6M8.5 14.5l3.5 2.6 3.5-2.6"/>`,
  meters: `<path ${F} d="M5 13h3v7H5zM10.5 8h3v12h-3zM16 4h3v16h-3z"/>`,
  menu: `<path d="M5 7h14M5 12h14M5 17h14"/>`,
  quest: `<path ${F} d="M7 3.5h10.5v14a3 3 0 0 1-3 3H6.5a3 3 0 0 1-3-3V16H7z"/><path d="M10 8h5M10 11.5h5"/>`,
  social: `<circle ${F} cx="9" cy="9" r="3"/><circle cx="16.5" cy="9.5" r="2.4"/><path ${F} d="M3.5 19c.5-3.3 2.6-5.2 5.5-5.2s5 1.9 5.5 5.2z"/><path d="M15 14.2c2.6-.3 4.8 1.3 5.4 4.8"/>`,
  skull: `<path ${F} d="M12 3.5c-4.4 0-7.5 3-7.5 7 0 2.4 1.2 4 2.8 5v3h9.4v-3c1.6-1 2.8-2.6 2.8-5 0-4-3.1-7-7.5-7z"/><circle cx="9.3" cy="11" r="1.5"/><circle cx="14.7" cy="11" r="1.5"/><path d="M10.5 18.5v-2M13.5 18.5v-2"/>`,
  clock: `<circle ${F} cx="12" cy="12" r="8"/><path d="M12 7.5V12l3 2"/>`,
  swords: `<path d="M4 4l9.5 9.5M20 4l-9.5 9.5"/><path ${F} d="M4 4h3.2l7.5 7.5-1.6 1.6L5.6 5.6zM20 4h-3.2l-7.5 7.5 1.6 1.6 7.5-7.5z"/><path d="M6 16.5l3.5 3.5M18 16.5 14.5 20M4.5 19.5l2.2-2.2M19.5 19.5l-2.2-2.2"/>`,
  key: `<circle ${F} cx="8" cy="12" r="3.8"/><path d="M11.8 12H20.5M17.5 12v3M20 12v2.2"/>`,
  chest: `<path ${F} d="M4 10h16v9.5H4z"/><path d="M4 10c0-3 2-5 4-5h8c2 0 4 2 4 5M4 13.5h16M12 12v3.5"/>`,
  sprint: `<path d="M13.5 4.5a1.8 1.8 0 1 0 0 .1M9 21l2.5-6 2.8 2.4V21M7.5 11l3-2.5h3.5l2.5 3 3 .5M11.5 15l1.3-4.2"/>`,
  potion: `<path ${F} d="M9.5 3.5h5v4.2c2.6 1 4.2 3.3 4.2 6.1A6.7 6.7 0 0 1 12 20.5a6.7 6.7 0 0 1-6.7-6.7c0-2.8 1.6-5.1 4.2-6.1z"/><path d="M8.5 3.5h7M6 14h12"/>`,
} as const;
export type HudGlyph = keyof typeof HUD_GLYPHS;

export function isHudGlyph(name: string): name is HudGlyph {
  return Object.prototype.hasOwnProperty.call(HUD_GLYPHS, name);
}

/** A menu glyph as an <svg> element. */
export function glyphSvg(name: HudGlyph, cls = ''): SVGSVGElement {
  return svgEl(
    `<svg class="hk-glyph ${cls}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round" aria-hidden="true">${HUD_GLYPHS[name]}</svg>`,
  );
}

/** Class emblems drawn in the crest's upper half (24×24 viewBox). */
export const CLASS_EMBLEM: Readonly<Record<string, string>> = {
  warden: `<path ${F} d="M12 3l7 2.6v5.6c0 4.6-3 7.9-7 9.8-4-1.9-7-5.2-7-9.8V5.6z"/><path d="M12 6v12M8 10.5h8"/>`,
  blademaster: `<path d="M5 19 17.5 6.5M19 19 6.5 6.5"/><path ${F} d="M17.5 6.5 19.5 4.5l-.4 2.4zM6.5 6.5 4.5 4.5l.4 2.4z"/><path d="M6.5 15.5l2 2M17.5 15.5l-2 2"/>`,
  lightbinder: `<circle ${F} cx="12" cy="12" r="4"/><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1"/>`,
  stormcaller: `<path ${F} d="M13.5 3 6 13.2h5L9.5 21 18 10.4h-5.2z"/>`,
};
const DEFAULT_EMBLEM = `<path ${F} d="M12 3.5l2.3 4.8 5.2.7-3.8 3.6.9 5.2L12 15.3l-4.6 2.5.9-5.2L4.5 9l5.2-.7z"/>`;

let crestSeq = 0;

/**
 * The circular crest medallion (100×100 viewBox): gold ring with cardinal points, dark disc,
 * class emblem in the upper half (the level number sits below it; see .hk-crest in theme.css).
 */
export function crestSvg(classId: string | null | undefined): SVGSVGElement {
  const id = `hkc${++crestSeq}`;
  const emblem = (classId && CLASS_EMBLEM[classId]) || DEFAULT_EMBLEM;
  return svgEl(`<svg viewBox="0 0 100 100" aria-hidden="true">
  <defs>
    <linearGradient id="${id}g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#f6e2a8"/><stop offset=".4" stop-color="#8a6a33"/><stop offset=".65" stop-color="#d6b26a"/><stop offset="1" stop-color="#6a4f22"/></linearGradient>
    <linearGradient id="${id}s" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#f2f4f8"/><stop offset=".5" stop-color="#6d7380"/><stop offset="1" stop-color="#c8cdd6"/></linearGradient>
    <radialGradient id="${id}d" cx=".5" cy=".38" r=".62"><stop offset="0" stop-color="#2c3242"/><stop offset=".7" stop-color="#0d1017"/><stop offset="1" stop-color="#05060a"/></radialGradient>
  </defs>
  <path d="M50 0 54 6 46 6zM100 50 94 54 94 46zM50 100 46 94 54 94zM0 50 6 46 6 54z" fill="url(#${id}g)"/>
  <circle cx="50" cy="50" r="45" fill="rgba(5,6,10,.55)" stroke="url(#${id}g)" stroke-width="2.4"/>
  <circle cx="50" cy="50" r="40.5" fill="url(#${id}d)" stroke="url(#${id}s)" stroke-width="1"/>
  <circle cx="50" cy="50" r="36" fill="none" stroke="rgba(214,178,106,.35)" stroke-width=".6" stroke-dasharray="1.5 3"/>
  <g transform="translate(36 16) scale(1.17)" fill="none" stroke="url(#${id}s)" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round" color="#c8cdd6">${emblem}</g>
</svg>`);
}

/** A bare <svg> element (for code that builds paths itself). */
export function emptySvg(viewBox = '0 0 24 24'): SVGSVGElement {
  const s = document.createElementNS(NS, 'svg');
  s.setAttribute('viewBox', viewBox);
  s.setAttribute('aria-hidden', 'true');
  return s;
}
